import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/lib/supabase";
import { isSupabaseConfigured } from "@/lib/env";
import { AuthContext, type AuthContextValue } from "./context";
import type { AppUser, Role } from "./types";

const DEV_USER_KEY = "bb_dev_user";
const ROLE_KEYS: Role[] = ["customer", "driver", "carrier", "internal"];

/** Sign a signed-in user out after this long with no interaction. */
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
/** Shared across tabs so activity in one tab keeps the others alive. */
const IDLE_ACTIVITY_KEY = "bb_last_activity";
/** Throttle how often we write the shared timestamp on continuous activity. */
const IDLE_WRITE_THROTTLE_MS = 5_000;
const IDLE_ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
  "wheel",
];

type SupabaseAuthUser = {
  id: string;
  email?: string;
  user_metadata?: Record<string, unknown>;
};

type ProfileRow = {
  role: Role | null;
  full_name: string | null;
  email: string | null;
  staff_level: AppUser["staffLevel"];
  department: AppUser["department"];
};

/**
 * Auth state. When Supabase is configured it uses real sessions; otherwise it
 * runs a local dev/mock flow so the portals can be previewed before a Supabase
 * project exists.
 *
 * IMPORTANT: the role shown in the client is for UX only. Authoritative access
 * control is enforced server-side via Supabase Row Level Security + edge
 * functions (docs/SECURITY.md sec 1). Never trust this value for security.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      const raw = localStorage.getItem(DEV_USER_KEY);
      setUser(raw ? (JSON.parse(raw) as AppUser) : null);
      setLoading(false);
      return;
    }

    const loadUser = async (authUser: SupabaseAuthUser | undefined) => {
      setUser(await mapSession(authUser));
      setLoading(false);
    };

    supabase.auth.getSession().then(({ data }) => {
      void loadUser(data.session?.user);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setTimeout(() => {
        void loadUser(session?.user);
      }, 0);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const signIn = useCallback<AuthContextValue["signIn"]>(
    async (email, password, devRole) => {
      if (!isSupabaseConfigured || !supabase) {
        const mockRole = devRole ?? "customer";
        const mockUser: AppUser = {
          id: "dev-" + mockRole,
          email,
          name: email.split("@")[0] || "User",
          role: mockRole,
          staffLevel: mockRole === "internal" ? "admin" : null,
          department: null,
        };
        localStorage.setItem(DEV_USER_KEY, JSON.stringify(mockUser));
        setUser(mockUser);
        return;
      }
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    },
    [],
  );

  const signOut = useCallback<AuthContextValue["signOut"]>(async () => {
    if (!isSupabaseConfigured || !supabase) {
      localStorage.removeItem(DEV_USER_KEY);
      setUser(null);
      return;
    }
    await supabase.auth.signOut();
  }, []);

  // Idle auto-logout: while someone is signed in, sign them out after
  // IDLE_TIMEOUT_MS with no interaction. Real activity (in ANY tab) resets the
  // clock; the visibility handler re-checks on wake/return so a slept machine or
  // backgrounded tab (where timers are throttled) still logs out on time.
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let lastWrite = 0;

    const getLast = () => Number(localStorage.getItem(IDLE_ACTIVITY_KEY) ?? "0");

    const arm = () => {
      if (timeoutId) clearTimeout(timeoutId);
      const remaining = IDLE_TIMEOUT_MS - (Date.now() - getLast());
      if (remaining <= 0) {
        void signOut();
        return;
      }
      timeoutId = setTimeout(() => void signOut(), remaining);
    };

    const markActivity = () => {
      const now = Date.now();
      if (now - lastWrite > IDLE_WRITE_THROTTLE_MS) {
        lastWrite = now;
        localStorage.setItem(IDLE_ACTIVITY_KEY, String(now));
      }
      arm();
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key === IDLE_ACTIVITY_KEY) arm();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") arm();
    };

    // Treat mount / fresh sign-in as activity so a stale timestamp can't log the
    // user out immediately, then start the countdown.
    localStorage.setItem(IDLE_ACTIVITY_KEY, String(Date.now()));
    arm();

    IDLE_ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, markActivity, { passive: true }));
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      IDLE_ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, markActivity));
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [userId, signOut]);

  const value = useMemo(
    () => ({ user, loading, signIn, signOut }),
    [user, loading, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

async function mapSession(u: SupabaseAuthUser | undefined): Promise<AppUser | null> {
  if (!u) return null;
  const profile = await getProfile(u.id);
  const role = safeRole(profile?.role) ?? "customer";

  return {
    id: u.id,
    email: profile?.email ?? u.email ?? "",
    name: profile?.full_name ?? (u.user_metadata?.name as string) ?? u.email?.split("@")[0] ?? "User",
    role,
    staffLevel: profile?.staff_level ?? null,
    department: profile?.department ?? null,
  };
}

async function getProfile(userId: string): Promise<ProfileRow | null> {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from("profiles")
    .select("role, full_name, email, staff_level, department")
    .eq("id", userId)
    .maybeSingle();
  if (error) return null;
  return data as ProfileRow | null;
}

function safeRole(value: unknown): Role | null {
  return ROLE_KEYS.includes(value as Role) ? (value as Role) : null;
}
